import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { DialogRoot, Play20Px, useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useInductionConfiguration } from "~/features/autostorePutaway/useInductionConfiguration";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { TempZone, tempZoneIconMap } from "~/lib/tempZone";

import { TemperatureZone } from "~/types/api";

export type Props = {
  open: boolean;
  onClose: () => void;
  onStart: (selectedZone: TemperatureZone) => void;
  isSubmitting?: boolean;
  /** Controls the button text and disabled state logic */
  variant: "start" | "change";
};

export function SelectTempZoneModal({
  open,
  onClose,
  onStart,
  isSubmitting = false,
  variant
}: Props) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { warningToast } = useToast();
  const { tempZones } = useGetMultiTempGridInfo();
  const { tempZone: savedTempZone, error: inductionConfigurationError } =
    useInductionConfiguration();
  const trackedModalName = useMixpanelModalName("Select Temp Zone");

  const [userSelectedZone, setUserSelectedZone] =
    useState<TemperatureZone | null>(null);
  const selectedZone = userSelectedZone || savedTempZone || null;

  useEffect(() => {
    if (inductionConfigurationError) {
      warningToast(getMessageFromRtkError(inductionConfigurationError));
    }
  }, [inductionConfigurationError, warningToast]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!open) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setUserSelectedZone(null);
    }
  }, [open]);

  const isChangeVariant = variant === "change";
  const isDifferentSelectionRequired =
    isChangeVariant && selectedZone === savedTempZone;

  const handleStart = () => {
    if (selectedZone) {
      mixpanelTrack({
        type: "Button Click",
        label: isChangeVariant ? "Confirm" : "Start",
        trackedPageName: trackedModalName,
        eventProperties: {
          "Temp Zone": selectedZone,
          "Previous Temp Zone": savedTempZone ?? "None"
        }
      });
      onStart(selectedZone);
    }
  };

  return (
    <DialogRoot
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={isSubmitting ? undefined : onClose}
    >
      <DialogTitle>{t("select temp zone")}</DialogTitle>

      <DialogContent sx={{ px: "60px" }}>
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "1.25rem",
            my: 2.5,
            mx: "auto",
            maxWidth: 260
          }}
        >
          {t("set workstation temp zones to induct products")}.
        </Typography>

        <Stack spacing={1.5}>
          {tempZones.map((zone) => {
            const normalizedZone = zone.toLowerCase() as TempZone;
            const Icon = tempZoneIconMap[normalizedZone];
            const isSelected = selectedZone === zone;
            const zoneColor = palette[normalizedZone].main;

            return (
              <Box
                key={zone}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!isSubmitting) {
                    setUserSelectedZone(zone);
                    mixpanelTrack({
                      type: "Select Option",
                      label: "Select Temp Zone",
                      trackedPageName: trackedModalName,
                      eventProperties: { "Temp Zone": zone }
                    });
                  }
                }}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: 3,
                  borderColor: isSelected ? "primary.main" : "grey.300",
                  cursor: "pointer"
                }}
              >
                <Radio
                  checked={isSelected}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: "35px"
                    }
                  }}
                  slotProps={{
                    input: {
                      "aria-label": `${zone} temperature zone`
                    }
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    bgcolor: zoneColor,
                    borderRadius: 1,
                    px: 2.5,
                    py: 1
                  }}
                >
                  <Typography sx={{ fontSize: "1.5rem" }}>
                    {capitalizeFirstLetter(zone.toLowerCase())}
                  </Typography>
                  {Icon && <Icon width={24} height={24} />}
                </Box>
              </Box>
            );
          })}
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 4,
            mt: 3
          }}
        >
          <Button
            size="large"
            variant="contained"
            color="secondary"
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => {
              mixpanelTrack({
                type: "Button Click",
                label: "Back",
                trackedPageName: trackedModalName
              });
              onClose();
            }}
            disabled={isSubmitting}
            sx={{ py: 1.5, px: 4, minWidth: "140px" }}
          >
            {t("back")}
          </Button>
          <Button
            size="large"
            variant="contained"
            color="primary"
            startIcon={isChangeVariant ? undefined : <Play20Px fill="white" />}
            disabled={
              !selectedZone || isSubmitting || isDifferentSelectionRequired
            }
            onClick={handleStart}
            sx={{ py: 1.5, px: 4, minWidth: "140px" }}
          >
            {isChangeVariant ? t("confirm") : t("start")}
          </Button>
        </Box>
      </DialogContent>
    </DialogRoot>
  );
}
